import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Button } from "./ui/button";
import { useConsent } from "../context/ConsentContext";

const Row = ({ title, desc, checked, onChange, disabled, testid }) => (
  <label className={`flex items-start gap-3 p-4 rounded-2xl border border-black/5 bg-secondary/40 ${disabled ? "opacity-70" : "cursor-pointer"}`}>
    <input type="checkbox" data-testid={testid} checked={checked} disabled={disabled} onChange={(e) => onChange && onChange(e.target.checked)} className="mt-1 w-4 h-4 accent-primary" />
    <span>
      <span className="font-semibold text-sm block">{title}</span>
      <span className="text-muted-foreground text-xs">{desc}</span>
    </span>
  </label>
);

export const CookieConsent = () => {
  const { decided, manageOpen, setManageOpen, acceptAll, rejectAll, savePrefs, consent } = useConsent();
  const [analytics, setAnalytics] = useState(false);
  const [functional, setFunctional] = useState(false);

  useEffect(() => { setAnalytics(consent.analytics); setFunctional(consent.functional); }, [manageOpen, consent]);

  const showBanner = !decided && !manageOpen;

  return (
    <>
      {showBanner && (
        <div data-testid="cookie-banner" className="fixed bottom-4 left-4 right-4 sm:left-6 sm:right-auto sm:max-w-md z-[60] animate-fade-up">
          <div className="bg-zinc-950 text-white rounded-2xl shadow-2xl border border-white/10 p-5">
            <div className="flex items-start gap-3">
              <Cookie className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="font-heading font-bold text-sm">Este site usa cookies</p>
                <p className="text-sm text-zinc-300 mt-1 leading-relaxed">
                  Usamos cookies essenciais ao funcionamento e, com o seu consentimento, cookies de análise e funcionais. Saiba mais na <Link to="/politica-cookies" className="text-primary underline underline-offset-2">Política de Cookies</Link>.
                </p>
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button data-testid="cookie-accept-all" onClick={acceptAll} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-9 px-4 text-sm">Aceitar tudo</Button>
                  <Button data-testid="cookie-reject-all" onClick={rejectAll} variant="outline" className="rounded-full h-9 px-4 text-sm font-semibold bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white">Rejeitar tudo</Button>
                  <Button data-testid="cookie-manage" onClick={() => setManageOpen(true)} variant="ghost" className="rounded-full h-9 px-3 text-sm text-zinc-300 hover:text-white hover:bg-white/10">Gerir preferências</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {manageOpen && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/60 p-4" onClick={() => setManageOpen(false)}>
          <div data-testid="cookie-manage-modal" className="bg-white rounded-3xl w-full max-w-lg p-6 max-h-[85vh] overflow-y-auto animate-fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading font-bold text-xl">Preferências de cookies</h2>
              <button data-testid="cookie-manage-close" onClick={() => setManageOpen(false)} className="p-1.5 rounded-full hover:bg-secondary transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-muted-foreground text-sm mb-5">
              Escolha que cookies autoriza. Os essenciais estão sempre ativos. Consulte a <Link to="/politica-cookies" onClick={() => setManageOpen(false)} className="text-primary underline underline-offset-2">Política de Cookies</Link>.
            </p>
            <div className="space-y-3">
              <Row title="Estritamente necessários" desc="Necessários ao funcionamento do site (sessão de administração e guardar as suas preferências). Sempre ativos." checked disabled />
              <Row title="Análise / Estatística" desc="Ajudam-nos a perceber como o site é utilizado (PostHog). Bloqueados até dar consentimento." checked={analytics} onChange={setAnalytics} testid="cookie-toggle-analytics" />
              <Row title="Funcionais" desc="Conteúdos incorporados de terceiros, como o mapa do Google." checked={functional} onChange={setFunctional} testid="cookie-toggle-functional" />
            </div>
            <div className="flex flex-wrap gap-2 mt-6">
              <Button data-testid="cookie-save" onClick={() => savePrefs({ analytics, functional })} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-10 px-5 flex-1 min-w-[160px]">Guardar preferências</Button>
              <Button data-testid="cookie-accept-all-modal" onClick={acceptAll} variant="outline" className="rounded-full h-10 px-5 font-semibold border-2">Aceitar tudo</Button>
              <Button data-testid="cookie-reject-all-modal" onClick={rejectAll} variant="ghost" className="rounded-full h-10 px-4 font-semibold">Rejeitar tudo</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
